import { ChangeDetectionStrategy, Component, DestroyRef, inject } from "@angular/core";

import { LgTranslateService } from "@logex/framework/lg-localization";
import { IFilterExportDefinition } from "@logex/framework/lg-exports";

import { ComponentType } from "@angular/cdk/portal";
import { LgFilterSet } from "../lg-filterset";
import { IFilterDefinition } from "../filter-definition";
import { IFilterRenderer, IFilterRendererFactory } from "../filter-renderer";
import { FilterRendererComponentBase } from "../filter-renderer-component-base";

// eslint-disable-next-line @typescript-eslint/no-unused-vars
export interface ITextInputFilterDefinition extends IFilterDefinition {
    filterType: "textInput";

    /**
     * Optional placeholder shown inside the input while empty. When unset no placeholder is
     * rendered, so existing definitions are unaffected.
     */
    placeholder?: string | undefined;

    /**
     * When true, render a full-width input with the label shown as its in-control placeholder
     * (matching the combo renderer's wide layout) instead of a separate label + narrow control.
     * Defaults to false, so existing definitions are unaffected.
     */
    wide?: boolean | undefined;
}

/**
 * Renderer for a filter based on text input.
 */
export class TextInputFilterRenderer implements IFilterRenderer {
    constructor(
        private _definition: ITextInputFilterDefinition,
        private _filters: any,
        lgTranslate: LgTranslateService
    ) {}

    createStorage(): void {
        if (this._filters[this._definition.storage!] === undefined) {
            this._filters[this._definition.storage!] = null;
        }
    }

    active(): boolean {
        return this._filters[this._definition.storage!] != null;
    }

    previewVisible(): boolean {
        return this._filters[this._definition.storage!] != null;
    }

    clear(): boolean {
        if (this._filters[this._definition.storage!] != null) {
            this._filters[this._definition.storage!] = null;
            return true;
        }
        return false;
    }

    getFilterLineComponent(): ComponentType<FilterRendererComponentBase<any, any>> {
        return TextInputFilterRendererLineComponent;
    }

    getPopupComponent(): ComponentType<FilterRendererComponentBase<any, any>> {
        return TextInputFilterRendererPopupComponent;
    }

    setValue(value: string | null): void {
        if (value?.trim() === "") {
            value = null;
        }
        this._filters[this._definition.storage!] = value;
    }

    getValue(): string {
        return this._filters[this._definition.storage!] ?? null;
    }

    getExportDefinition(): IFilterExportDefinition {
        return {
            name: this._definition.name ?? "",
            activeFn: () => this.active(),
            exportFn: () => [this.getValue()]
        };
    }

    serialize(): string | null {
        if (!this.active()) return null;
        return this._filters[this._definition.storage!];
    }

    deserialize(state: string): boolean {
        const oldState = this._filters[this._definition.storage!];
        this._filters[this._definition.storage!] = state;
        return state !== oldState;
    }
}

// Factory ---------------------------------------------------------------------------------------------------------
export class TextInputFilterRendererFactory<T> implements IFilterRendererFactory {
    readonly name: string = "textInput";

    create(
        definition: ITextInputFilterDefinition,
        filters: Record<string, any>,
        _definitions: IFilterDefinition[],
        filterSet: LgFilterSet
    ): IFilterRenderer {
        return new TextInputFilterRenderer(definition, filters, filterSet.lgTranslate);
    }
}

// Line template  --------------------------------------------------------------------------------------------------
@Component({
    standalone: false,
    selector: "lg-text-input-filter-renderer-line",
    // eslint-disable-next-line @angular-eslint/component-max-inline-declarations
    template: `
        @if (!_definition.wide) {
            <label>{{ _definition.label }}:</label>
            <div class="control">
                <input
                    lgSelectOnFocus
                    type="text"
                    class="input"
                    placeholder="{{ _definition.placeholder }}"
                    [value]="_renderer.getValue()"
                    (input)="_change($event)"
                />
            </div>
        }
        @if (_definition.wide) {
            <div class="filter__control filter__control--wide">
                <input
                    lgSelectOnFocus
                    type="text"
                    class="input"
                    placeholder="{{ _definition.placeholder ?? _definition.label }}"
                    [value]="_renderer.getValue()"
                    (input)="_change($event)"
                />
            </div>
        }
    `,
    styles: [
        // The filter placeholder doubles as the field hint (and, in wide mode, the label). Match
        // the combo renderer's in-frame placeholder colour (gray-color-60) rather than the lighter
        // default input placeholder (gray-color-20), so text and combo filters read alike in both
        // wide and non-wide layouts.
        ".input::placeholder { color: rgb(var(--gray-color-60)); }"
    ],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class TextInputFilterRendererLineComponent extends FilterRendererComponentBase<
    ITextInputFilterDefinition,
    TextInputFilterRenderer
> {
    _change(event: Event): void {
        this._renderer.setValue((event.target as HTMLInputElement).value);
        this._triggerChange();
    }
}

// Popup template --------------------------------------------------------------------------------------------------
@Component({
    standalone: false,
    selector: "lg-text-input-filter-renderer-popup",
    // eslint-disable-next-line @angular-eslint/component-max-inline-declarations
    template: `
        <div class="header">
            {{ _definition.name }}
        </div>
        <input
            lgDefaultFocus
            lgSelectOnFocus
            type="text"
            class="input"
            [value]="_renderer.getValue()"
            (change)="_change($event)"
        />
    `,
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class TextInputFilterRendererPopupComponent<T> extends FilterRendererComponentBase<
    ITextInputFilterDefinition,
    TextInputFilterRenderer
> {
    _change(event: Event): void {
        this._renderer.setValue((event.target as HTMLInputElement).value);
        this._triggerChange();
    }
}
